import Vue from "vue";
import { ActionTree, MutationTree } from "vuex";
import { RootState } from "@/store/types";
import { ServerResult } from "@/core/http/server_result";
import moment from "moment";
import ExcelJS from "exceljs";

/**
 * СОХ: зоны, для которых всегда резервируем колонки (переносы с СГП), даже без факта/плана.
 * Имена должны совпадать с `delivery_zone_groups.name` в БД.
 */
export const SHIP_FORECAST_SOH_ZONE_NAMES = [
  "Омск",
  "Екатеринбург",
  "Тюмень",
];

export type ShipmentForecastMonthPeriodMode = "calendar" | "rolling";

/** Единый период для режима «Месяц»: календарный месяц от targetDates или скользящий месяц от «сегодня». */
export function resolveShipmentForecastMonthPeriod(
  targetDates: Date | string | null | undefined,
  monthFromToday: boolean,
): {
  dateFrom: string;
  dateTo: string;
  mode: ShipmentForecastMonthPeriodMode;
  label: string;
} {
  if (monthFromToday) {
    const anchor = moment().startOf("day");
    const dateFrom = anchor.format("YYYY-MM-DD");
    const dateTo = anchor
      .clone()
      .add(1, "month")
      .subtract(1, "day")
      .format("YYYY-MM-DD");
    const label = `${anchor.format("DD.MM.YYYY")} — ${moment(
      dateTo,
      "YYYY-MM-DD",
    ).format("DD.MM.YYYY")}`;
    return { dateFrom, dateTo, mode: "rolling", label };
  }
  const t = targetDates ? moment(targetDates) : moment();
  const dateFrom = t.clone().startOf("month").format("YYYY-MM-DD");
  const dateTo = t.clone().endOf("month").format("YYYY-MM-DD");
  const label = `${moment(dateFrom, "YYYY-MM-DD").format(
    "DD.MM.YYYY",
  )} — ${moment(dateTo, "YYYY-MM-DD").format("DD.MM.YYYY")}`;
  return { dateFrom, dateTo, mode: "calendar", label };
}

function emptyZoneCell(zoneId: number | null) {
  return {
    zone_id: zoneId,
    plan: 0,
    fact_tk: 0,
    fact_deal: 0,
    fact_movement: 0,
    movement: 0,
    base_weight: 0,
    deal_stats: { stats: [] },
    move_stats: { stats: [] },
    tk_stats: { stats: [] },
  };
}

/** Нулевые ячейки по всем зонам колонки-даты (строки тары без движений иначе дают пустой <td>). */
function padShipmentItemZonesForDates(
  item: any,
  datesColMap: Record<string, any>,
) {
  Object.keys(datesColMap).forEach((itemDate) => {
    const col = datesColMap[itemDate];
    if (!col || col.type !== "date") return;
    if (!item.dates[itemDate]) {
      item.dates[itemDate] = { total: 0 };
    }
    const t = item.dates[itemDate].total;
    if (t == null || Number.isNaN(Number(t))) {
      item.dates[itemDate].total = 0;
    }
    const zoneNames = col.zones || [];
    zoneNames.forEach((zn: string) => {
      if (item.dates[itemDate][zn] != null) return;
      const cz = col.colspan_zones?.[zn];
      const idRaw = cz?.id;
      const zid =
        idRaw != null && idRaw !== ""
          ? Number(idRaw)
          : null;
      item.dates[itemDate][zn] = emptyZoneCell(
        zid != null && !Number.isNaN(zid) ? zid : null,
      );
    });
  });
}

interface ShipmentForecastByDateState {
  loaded: boolean;
  loading: boolean;
  fatalError: boolean;

  filter: {
    dZoneGroups: any;
    targetDates: Date;
  };

  items: any[];
  data: any[];
  dates: any[];
  clients: any[];
  /** Режим таблицы из последнего запроса (week | day | month). */
  viewMode: string;
  reportDateFrom: string | null;
  reportDateTo: string | null;
  transferLoadingByDate: Record<string, boolean>;
  transferLoadingBySourceDate: Record<string, boolean>;
  greenDatesWithData: Record<string, boolean>;
}

const state: ShipmentForecastByDateState = {
  loading: false,
  loaded: false,
  fatalError: false,

  filter: {
    dZoneGroups: null,
    targetDates: new Date(),
  },

  items: [],
  data: [],
  dates: [],
  clients: [],
  viewMode: "week",
  reportDateFrom: null,
  reportDateTo: null,
  transferLoadingByDate: {},
  transferLoadingBySourceDate: {},
  greenDatesWithData: {},
};

function buildFilter(params: any) {
  const queryParams: any = {};

  if (params.targetDates) {
    queryParams.date_from = moment(params.targetDates)
      .startOf("month")
      .format("YYYY-MM-DD");
    queryParams.date_to = moment(params.targetDates)
      .startOf("month")
      .add(1, "month")
      .subtract(1, "day")
      .format("YYYY-MM-DD");
  }

  if (params.dZoneGroups) {
    queryParams.delivery_zone_id = params.dZoneGroups;
  }

  if (params.sortField !== null) {
    queryParams.order_by = params.sortField;
    queryParams.order_type = params.sortDirection;
  }

  return queryParams;
}

export const REPORT_DATA = "REPORT_DATA";
export const CLIENT_LIST = "CLIENT_LIST";
export const MONTH_CELL_DETAIL = "MONTH_CELL_DETAIL";

/** Извлекает числовой id из значения (число или строка "d:entity:N"). */
export function numericId(value: any): number | null {
  if (value == null || value === "") return null;
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  const s = String(value);
  const match = s.match(/\d+$/);
  return match ? parseInt(match[0], 10) : null;
}

function transferLoadingKey(
  sourceDate: string,
  targetDate: string,
  deliveryZoneGroupId?: number | null,
  greenClientId?: number | null,
): string {
  const base = `${sourceDate}->${targetDate}`;
  if (
    deliveryZoneGroupId != null &&
    greenClientId != null
  ) {
    return `${base}:z:${deliveryZoneGroupId}:g:${greenClientId}`;
  }
  if (deliveryZoneGroupId != null) return `${base}:z:${deliveryZoneGroupId}`;
  if (greenClientId != null) return `${base}:g:${greenClientId}`;
  return base;
}

function toDateKey(value: any): string | null {
  if (!value) return null;
  if (typeof value === "string") return value.slice(0, 10);
  const m = moment(value);
  return m.isValid() ? m.format("YYYY-MM-DD") : null;
}

function extractPlanIds(rawPlanIds: any): any[] {
  if (!Array.isArray(rawPlanIds) || rawPlanIds.length === 0) return [];
  return rawPlanIds.map((id: any) =>
    typeof id === "object" && id?.pk != null ? id.pk : id,
  );
}

function flattenStatsInner(statsWrapper: any): any[] {
  if (!statsWrapper) return [];
  const inner = statsWrapper.stats;
  if (!Array.isArray(inner)) return [];
  if (inner.length > 0 && Array.isArray(inner[0])) return inner[0];
  return inner;
}

function mergeStatsWrappers(a: any, b: any): any {
  const aa = flattenStatsInner(a);
  const bb = flattenStatsInner(b);
  if (!aa.length) return b || a;
  if (!bb.length) return a;
  return { stats: [...aa, ...bb] };
}

/** Суммирует поля ячеек зоны по всем дням месяца; stats склеиваются для детализации. */
function mergeZoneDataCells(cells: any[]): any {
  if (!cells.length) return {};
  const num = (x: any) => parseFloat(x) || 0;
  const out = { ...cells[0] };
  for (let i = 1; i < cells.length; i++) {
    const z = cells[i];
    out.plan = num(out.plan) + num(z.plan);
    out.fact_tk = num(out.fact_tk) + num(z.fact_tk);
    out.fact_deal = num(out.fact_deal) + num(z.fact_deal);
    out.fact_movement = num(out.fact_movement) + num(z.fact_movement);
    out.movement = num(out.movement) + num(z.movement);
    out.deal_stats = mergeStatsWrappers(out.deal_stats, z.deal_stats);
    out.move_stats = mergeStatsWrappers(out.move_stats, z.move_stats);
    out.tk_stats = mergeStatsWrappers(out.tk_stats, z.tk_stats);
  }
  return out;
}

/**
 * Одна виртуальная строка delivery_date со всеми зонами, сумма по календарным дням [dateFrom..dateTo] включительно.
 * В режиме «Месяц» dateFrom/dateTo — границы календарного месяца (без дней соседних месяцев).
 */
function mergeItemDatesToSingleRow(
  item_dates: Record<string, any>,
  dateFrom: string,
  dateTo: string,
): { zones: any[] } {
  const zoneMap: Record<
    string,
    { zone_name: string; zone_id: any; cells: any[] }
  > = {};
  const from = moment(dateFrom);
  const to = moment(dateTo);
  if (!from.isValid() || !to.isValid()) return { zones: [] };
  for (let d = from.clone(); !d.isAfter(to); d.add(1, "day")) {
    const key = d.format("YYYY-MM-DD");
    const row = item_dates[key];
    if (!row?.zones?.length) continue;
    for (const zone of row.zones) {
      const zn = zone.zone_name;
      if (!zoneMap[zn]) {
        zoneMap[zn] = { zone_name: zn, zone_id: zone.zone_id, cells: [] };
      }
      if (zone.data?.[0]) zoneMap[zn].cells.push(zone.data[0]);
    }
  }
  const zones = Object.values(zoneMap).map((z) => ({
    zone_name: z.zone_name,
    zone_id: z.zone_id,
    data: [mergeZoneDataCells(z.cells)],
  }));
  return { zones };
}

function shouldIncludeGreenClientColumns(
  viewMode: string,
  dateEntry: any,
  greenDatesWithData: Record<string, boolean>,
): boolean {
  if (viewMode === "month") return true;
  const dateKey = dateEntry?.date;
  if (!dateKey) return false;
  return !!greenDatesWithData[dateKey];
}

/** Показывать колонку клиента для даты: есть план или факт по этой дате (из БД). */
export function greenClientHasColumnData(
  client: any,
  dateKey: string | null | undefined,
  viewMode: string,
  _dates: any[],
  dateEntry: any,
): boolean {
  if (!dateKey) return false;
  const monthAgg = viewMode === "month" && !!dateEntry?.isMonthAggregate;
  if (monthAgg) {
    return Number(client?.plan || 0) + Number(client?.fact || 0) > 0;
  }
  let planSum = 0;
  let factSum = 0;
  Object.values(client?.stikers || {}).forEach((st: any) => {
    Object.values(st?.products || {}).forEach((prod: any) => {
      planSum += Number(prod?.plan_by_date?.[dateKey] || 0);
      factSum += Number(prod?.fact_by_date?.[dateKey] || 0);
    });
  });
  return planSum + factSum > 0;
}

function countGreenClientColumnsForDate(
  clients: any[],
  viewMode: string,
  dates: any[],
  dateEntry: any,
  greenDatesWithData: Record<string, boolean>,
): number {
  if (
    !shouldIncludeGreenClientColumns(
      viewMode,
      dateEntry,
      greenDatesWithData,
    )
  ) {
    return 0;
  }
  const dateKey = dateEntry?.date;
  if (!dateKey) return 0;
  return clients.filter((c) =>
    greenClientHasColumnData(c, dateKey, viewMode, dates, dateEntry),
  ).length;
}

function greenClientPlanTotalForDate(
  client: any,
  dateKey: string,
  viewMode: string,
  dateEntry: any,
): number {
  if (viewMode === "month" && dateEntry?.isMonthAggregate) {
    return Number(client?.plan || 0);
  }
  let t = 0;
  Object.values(client?.stikers || {}).forEach((st: any) => {
    Object.values(st?.products || {}).forEach((prod: any) => {
      t += Number(prod?.plan_by_date?.[dateKey] || 0);
    });
  });
  return t;
}

function greenClientFactTotalForDate(
  client: any,
  dateKey: string,
  viewMode: string,
  dateEntry: any,
): number {
  if (viewMode === "month" && dateEntry?.isMonthAggregate) {
    return Number(client?.fact || 0);
  }
  let t = 0;
  Object.values(client?.stikers || {}).forEach((st: any) => {
    Object.values(st?.products || {}).forEach((prod: any) => {
      t += Number(prod?.fact_by_date?.[dateKey] || 0);
    });
  });
  return t;
}

function greenStickerPlanForDate(
  client: any,
  stickerId: any,
  dateKey: string,
  viewMode: string,
  dateEntry: any,
): number {
  if (viewMode === "month" && dateEntry?.isMonthAggregate) {
    return Number(client?.stikers?.[stickerId]?.plan || 0);
  }
  const products = client?.stikers?.[stickerId]?.products || {};
  let t = 0;
  Object.values(products).forEach((p: any) => {
    t += Number(p?.plan_by_date?.[dateKey] || 0);
  });
  return t;
}

function greenStickerFactForDate(
  client: any,
  stickerId: any,
  dateKey: string,
  viewMode: string,
  dateEntry: any,
): number {
  if (viewMode === "month" && dateEntry?.isMonthAggregate) {
    return Number(client?.stikers?.[stickerId]?.fact || 0);
  }
  const products = client?.stikers?.[stickerId]?.products || {};
  let t = 0;
  Object.values(products).forEach((p: any) => {
    t += Number(p?.fact_by_date?.[dateKey] || 0);
  });
  return t;
}

function planDocMatchesProductRow(
  plan: any,
  productContainerId: any,
  stickerId: any,
): boolean {
  const pPid = numericId(plan.product_container_id);
  const pidRaw = numericId(productContainerId);
  if (pPid == null || pidRaw == null || pPid !== pidRaw) return false;
  const sticker_ids = plan.sticker_ids || [];
  const planSid =
    sticker_ids.length > 0
      ? parseInt(String(sticker_ids[0]).replace("d:voc_stickers:", ""), 10)
      : null;
  const sidRaw =
    stickerId == null || stickerId === "" ? null : numericId(stickerId);
  if (sidRaw == null && planSid == null) return true;
  if (sidRaw == null || planSid == null) return false;
  return planSid === sidRaw;
}

/** Согласовано с getGreenCellPlanValue (ShipmentForecastByDate.vue); clients — с полем plans до stikers. */
function greenPlanFromClientPlanDoc(
  plan: any,
  dateKey: string,
  viewMode: string,
  dateEntry: any,
): number {
  if (viewMode === "month" && dateEntry?.isMonthAggregate) {
    return Number(plan.plan ?? 0);
  }
  const byDate = plan.plan_by_date || {};
  const v = byDate[dateKey];
  if (v != null && v !== "") return Number(v);
  return 0;
}

function greenFactFromClientPlanDoc(
  plan: any,
  dateKey: string,
  viewMode: string,
  dateEntry: any,
): number {
  if (viewMode === "month" && dateEntry?.isMonthAggregate) {
    return Number(plan.fact ?? 0);
  }
  const byDate = plan.fact_by_date || {};
  const v = byDate[dateKey];
  if (v != null && v !== "") return Number(v);
  return 0;
}

function greenStickerBucketFromClient(
  client: any,
  stickerId: any,
): any | null {
  const st = client?.stikers;
  if (!st) return null;
  if (st[stickerId] != null) return st[stickerId];
  const n = numericId(stickerId);
  if (n != null) {
    if (st[n] != null) return st[n];
    if (st[String(n)] != null) return st[String(n)];
  }
  return null;
}

function productContainerKeyForGreen(productContainerId: any): string {
  if (typeof productContainerId === "number") return String(productContainerId);
  return String(productContainerId || "").replace("d:product_containers:", "");
}

/**
 * Сумма плана зелёных клиентов по таре × стикеру — вычитается из «Остаток».
 * Источник: `client.stikers` (как getGreenCellPlanValue в UI). Ранее использовался
 * `client.plans` + planDocMatchesProductRow — рассинхрон ключей стикера давал недовычет плана.
 */
function sumGreenPlanForProductRow(
  clients: any[],
  stickerId: any,
  productContainerId: any,
  dateKey: string,
  viewMode: string,
  dateEntry: any,
): number {
  let planSum = 0;
  if (!clients?.length) return planSum;
  const pid = productContainerKeyForGreen(productContainerId);
  const monthAgg = viewMode === "month" && !!dateEntry?.isMonthAggregate;
  clients.forEach((client: any) => {
    const bucket = greenStickerBucketFromClient(client, stickerId);
    const prod = bucket?.products?.[pid];
    if (!prod) return;
    if (monthAgg) {
      planSum += Number(prod.plan ?? 0);
      return;
    }
    planSum += Number(prod.plan_by_date?.[dateKey] || 0);
  });
  return planSum;
}

/**
 * Преобразует документы batch production_forecast в формат clients_list:
 * учёт в разрезе тары продукта (product_container_id), внутри — по стикерам.
 * Итоги по стикеру = сумма по тарам этого стикера, итог по клиенту = сумма по всем ячейкам.
 */
function buildClientsListFromProductionForecast(
  documents: any[],
  _dateFrom?: string | null,
  _dateTo?: string | null,
  calendarMonthBounds?: { from: string; to: string } | null,
): any[] {
  if (!documents || documents.length === 0) return [];

  const byClient = documents.reduce((acc: any, row: any) => {
    const rawDate = row.period_start ?? row.date_from ?? row.date_to;
    const dateKey = toDateKey(rawDate);
    if (calendarMonthBounds) {
      if (
        !dateKey ||
        dateKey < calendarMonthBounds.from ||
        dateKey > calendarMonthBounds.to
      ) {
        return acc;
      }
    }
    const cid = row.client_id;
    if (!acc[cid]) {
      acc[cid] = {
        id: cid,
        name: row.client_name,
        delivery_zone_group_id: row.delivery_zone_group_id ?? null,
        plan: 0,
        fact: 0,
        plan_manual: 0,
        plan_idsSet: new Set<any>(),
        byCell: {} as any,
      };
    }
    if (
      acc[cid].delivery_zone_group_id == null &&
      row.delivery_zone_group_id != null
    ) {
      acc[cid].delivery_zone_group_id = row.delivery_zone_group_id;
    }
    const pid = numericId(row.product_container_id);
    const sid = numericId(row.sticker_id);
    const key = `${pid ?? 0}_${sid ?? 0}`;
    if (!acc[cid].byCell[key]) {
      acc[cid].byCell[key] = {
        product_container_id: pid,
        sticker_id: sid,
        product_name: row.product_name || "",
        product_container_name: row.product_container_name || "",
        plan: 0,
        fact: 0,
        plan_manual: 0,
        plan_ids_by_date: {} as Record<string, number[]>,
        plan_by_date: {} as Record<string, number>,
        fact_by_date: {} as Record<string, number>,
      };
    }
    const num = (x: any) => (x != null && x !== "" ? parseFloat(String(x)) : 0);
    const planVal = num(row.final_quantity);
    const factVal = num(row.fact_quantity);
    const manualVal = num(row.manual_quantity);
    acc[cid].plan += planVal;
    acc[cid].fact += factVal;
    acc[cid].plan_manual += manualVal;
    acc[cid].byCell[key].plan += planVal;
    acc[cid].byCell[key].fact += factVal;
    acc[cid].byCell[key].plan_manual += manualVal;
    if (dateKey) {
      const arr = extractPlanIds(row.plan_ids);
      if (!acc[cid].byCell[key].plan_ids_by_date[dateKey]) {
        acc[cid].byCell[key].plan_ids_by_date[dateKey] = [];
      }
      acc[cid].byCell[key].plan_ids_by_date[dateKey].push(...arr);
      acc[cid].byCell[key].plan_by_date[dateKey] =
        (acc[cid].byCell[key].plan_by_date[dateKey] || 0) + planVal;
      acc[cid].byCell[key].fact_by_date[dateKey] =
        (acc[cid].byCell[key].fact_by_date[dateKey] || 0) + factVal;
    }
    const ids = extractPlanIds(row.plan_ids);
    if (ids.length) ids.forEach((id: any) => acc[cid].plan_idsSet.add(id));
    return acc;
  }, {});

  return Object.values(byClient)
    .map((c: any) => {
      const plans = Object.values(c.byCell)
        .filter(
          (cell: any) =>
            cell.product_container_id != null || cell.sticker_id != null,
        )
        .map((cell: any) => ({
          product_container_id:
            cell.product_container_id != null
              ? `d:product_containers:${cell.product_container_id}`
              : null,
          plan: cell.plan,
          fact: cell.fact,
          plan_manual: cell.plan_manual,
          sticker_ids:
            cell.sticker_id != null
              ? [`d:voc_stickers:${cell.sticker_id}`]
              : [],
          plan_ids_for_date: cell.plan_ids_by_date || {},
          plan_by_date: cell.plan_by_date || {},
          fact_by_date: cell.fact_by_date || {},
          product_name: cell.product_name || "",
          product_container_name: cell.product_container_name || "",
        }));
      const planIds = Array.from(c.plan_idsSet);
      return {
        id: c.id,
        name: c.name,
        delivery_zone_group_id: c.delivery_zone_group_id ?? null,
        plan: c.plan,
        fact: c.fact,
        plan_manual: c.plan_manual,
        plans,
        plan_ids: planIds,
      };
    })
    .sort((a: any, b: any) => (a.name || "").localeCompare(b.name || "", "ru"));
}

function rawField(row: any, key: string): any {
  if (!row || typeof row !== "object") return null;
  if (row[key] !== undefined) return row[key];
  return row[key.toString()] ?? null;
}

function toNumberOrZero(value: any): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Строки сделок для детализации fact_tk (неделя/день). */
function normalizeFactTkDealRows(rows: any): any[] {
  if (!Array.isArray(rows)) return [];
  return rows.map((row: any) => ({
    date: rawField(row, "date"),
    week_num: rawField(row, "week_num"),
    deal_id: rawField(row, "deal_id"),
    type_id: rawField(row, "type_id"),
    deal_product_container_id: rawField(row, "deal_product_container_id"),
    container_quantity: toNumberOrZero(rawField(row, "container_quantity")),
    container_weight: toNumberOrZero(rawField(row, "container_weight")),
    weight_kg: toNumberOrZero(rawField(row, "weight_kg")),
  }));
}

function normalizeMonthDetailRows(rows: any): any[] {
  if (!Array.isArray(rows)) return [];
  return rows.map((row: any) => ({
    date: rawField(row, "date"),
    created_at: rawField(row, "created_at"),
    change_type: rawField(row, "change_type"),
    transfer_source_date: rawField(row, "transfer_source_date"),
    transfer_target_date: rawField(row, "transfer_target_date"),
    employee_name: rawField(row, "employee_name"),
    plan_id: rawField(row, "plan_id"),
    client_id: rawField(row, "client_id"),
    delivery_zone_group_id: rawField(row, "delivery_zone_group_id"),
    product_container_id: rawField(row, "product_container_id"),
    calculated_quantity: toNumberOrZero(rawField(row, "calculated_quantity")),
    manual_quantity:
      rawField(row, "manual_quantity") == null
        ? null
        : toNumberOrZero(rawField(row, "manual_quantity")),
    final_quantity: toNumberOrZero(rawField(row, "final_quantity")),
    fact_quantity: toNumberOrZero(rawField(row, "fact_quantity")),
    calc_stats: rawField(row, "calc_stats") || [],
  }));
}

function normalizeMonthDetailDocument(document: any): any {
  const dailyRows = normalizeMonthDetailRows(rawField(document, "daily_rows"));
  const history = normalizeMonthDetailRows(rawField(document, "history"));
  const totalsSrc = rawField(document, "totals") || {};

  return {
    detail_type: rawField(document, "detail_type") || "",
    daily_rows: dailyRows,
    deal_rows: normalizeFactTkDealRows(rawField(document, "deal_rows")),
    history,
    totals: {
      calculated_quantity: toNumberOrZero(rawField(totalsSrc, "calculated_quantity")),
      manual_quantity: toNumberOrZero(rawField(totalsSrc, "manual_quantity")),
      final_quantity: toNumberOrZero(rawField(totalsSrc, "final_quantity")),
      fact_quantity: toNumberOrZero(rawField(totalsSrc, "fact_quantity")),
    },
    calc_basis: rawField(document, "calc_basis") || {},
  };
}

export const actions: ActionTree<ShipmentForecastByDateState, RootState> = {
  loadData({ commit, state, dispatch }, params) {
    const vm = (this as any)._vm;

    const queryParams: any = {};
    let greenTargetDate: string | undefined;

    if (params.targetDates) {
      const viewMode = params.viewMode || "week";
      const target = moment(params.targetDates);
      switch (viewMode) {
        case "day":
          queryParams.date_from = target.format("YYYY-MM-DD");
          queryParams.date_to = target.format("YYYY-MM-DD");
          break;
        case "month": {
          const period = resolveShipmentForecastMonthPeriod(
            params.targetDates,
            !!params.monthFromToday,
          );
          queryParams.date_from = period.dateFrom;
          queryParams.date_to = period.dateTo;
          greenTargetDate = period.dateFrom;
          if (typeof console !== "undefined" && console.debug) {
            console.debug("[ShipmentForecastByDate] month period", {
              monthFromToday: !!params.monthFromToday,
              mode: period.mode,
              dateFrom: period.dateFrom,
              dateTo: period.dateTo,
              anchor: moment().format("YYYY-MM-DD"),
            });
          }
          break;
        }
        default:
          // week: Monday–Sunday (7 days, ISO week)
          queryParams.date_from = target
            .clone()
            .startOf("isoWeek")
            .format("YYYY-MM-DD");
          queryParams.date_to = target
            .clone()
            .endOf("isoWeek")
            .format("YYYY-MM-DD");
      }
    }
    if (params.employee_id) {
      queryParams.employee_id = params.employee_id;
    }
    if (params.client_name) {
      queryParams.client_name = params.client_name;
    }
    if (params.client_id) {
      queryParams.client_id = params.client_id;
    }
    if (params.with_archived) {
      queryParams.with_archived = params.with_archived;
    }

    return new Promise<void>((resolve, reject) => {
      if (Object.values(params).length > 0) {
        commit("START_LOADING");

        // Зелёные клиенты: те же данные, что и в production-forecast (batch production_forecast)
        const viewMode = params.viewMode || "week";
        const targetDate = greenTargetDate ?? queryParams.date_from;
        const greenViewMode: string = viewMode;
        const greenClientParams = {
          target_date: targetDate,
          view_mode: greenViewMode,
          compact: false,
        };

        if (typeof console !== "undefined" && console.debug) {
          console.debug("[ShipmentForecastByDate] query range", {
            viewMode,
            date_from: queryParams.date_from,
            date_to: queryParams.date_to,
            green_target_date: targetDate,
          });
        }

        vm.$db([
          vm
            .$q(REPORT_DATA)
            .raw("shipment_forecast_by_date")
            .action(queryParams),
          vm
            .$q(CLIENT_LIST)
            .raw("production_forecast")
            .action(greenClientParams),
        ]).then((result: ServerResult) => {
          const productionForecastContent = result
            .getOperation(CLIENT_LIST)
            .getContent();
          const calendarMonthBounds =
            viewMode === "month"
              ? (() => {
                  const period = resolveShipmentForecastMonthPeriod(
                    params.targetDates,
                    !!params.monthFromToday,
                  );
                  return { from: period.dateFrom, to: period.dateTo };
                })()
              : null;
          const clientsList = buildClientsListFromProductionForecast(
            productionForecastContent.documents || [],
            productionForecastContent.date_from,
            productionForecastContent.date_to,
            calendarMonthBounds,
          );
          let reportDateFrom = queryParams.date_from;
          let reportDateTo = queryParams.date_to;
          if (viewMode === "month") {
            const period = resolveShipmentForecastMonthPeriod(
              params.targetDates,
              !!params.monthFromToday,
            );
            reportDateFrom = period.dateFrom;
            reportDateTo = period.dateTo;
          }
          commit("SET_REPORT_DATA", {
            ...result.getOperation(REPORT_DATA).getContent(),
            date_from: reportDateFrom,
            date_to: reportDateTo,
            clients_list: clientsList,
            viewMode: viewMode,
          });
          commit("SET_ITEMS", null);
          commit("LOADED_SUCCESS");

          resolve();
        }).catch((error: any) => {
          commit("LOADED_ERROR");
          reject(error);
        });
      } else {
        resolve();
      }
    });
  },

  loadMonthCellDetail(_ctx, params: any) {
    const vm = (this as any)._vm;
    const payload = {
      ...params,
      detail_type: params?.detail_type,
      date_from: params?.date_from,
      date_to: params?.date_to,
    };

    if (typeof console !== "undefined" && console.debug) {
      console.debug("shipment-forecast-by-date:month-detail:request", payload);
    }

    return vm
      .$db([
        vm
          .$q(MONTH_CELL_DETAIL)
          .raw("shipment_forecast_by_date.month_detail")
          .action(payload),
      ])
      .then((result: ServerResult) => {
        const content = result.getOperation(MONTH_CELL_DETAIL).getContent();
        const document = content?.documents?.[0] || {};
        const normalized = normalizeMonthDetailDocument(document);

        if (typeof console !== "undefined" && console.debug) {
          console.debug("shipment-forecast-by-date:month-detail:response", {
            detail_type: normalized.detail_type,
            daily_rows: normalized.daily_rows.length,
            deal_rows: normalized.deal_rows?.length ?? 0,
            history: normalized.history.length,
          });
        }

        return normalized;
      })
      .catch((error: any) => {
        if (typeof console !== "undefined" && console.error) {
          console.error("shipment-forecast-by-date:month-detail:error", {
            detail_type: payload.detail_type,
            date_from: payload.date_from,
            date_to: payload.date_to,
            error: error?.message || error,
          });
        }
        throw error;
      });
  },

  exportData({ state, getters }, params) {
    const viewMode = params.viewMode || state.viewMode || "week";
    if (viewMode === "month" && typeof console !== "undefined" && console.debug) {
      console.debug("[ShipmentForecastByDate] export month", {
        datesLength: state.dates.length,
        date_from: state.reportDateFrom,
        date_to: state.reportDateTo,
      });
    }
    const start_date = moment(params.targetDates);
    const xlsxNegStyle = (cell: any, raw: any) => {
      const n = parseFloat(raw);
      if (!Number.isNaN(n) && n < 0) {
        cell.font = {
          ...(cell.font || {}),
          color: { argb: "FFFF0000" },
        };
      }
    };
    const zonePlanVisibleExport = (zz: any, z: any) =>
      !!(
        zz?.any_plan_or_fact_deal_cell ||
        (z && (Number(z.plan) > 0 || Number(z.fact_deal) > 0))
      );
    const zoneTkVisibleExport = (zz: any, z: any) =>
      !!(zz?.any_fact_tk_cell || (z && Number(z.fact_tk) > 0));
    const showFactExport = !!params.show_fact;
    const zoneMoveVisibleExport = (_zz: any, z: any) => {
      if (!z) return false;
      const m = Number(z.movement) || 0;
      const fm = Number(z.fact_movement) || 0;
      if (showFactExport) {
        return Math.abs(m) > 1e-9 || Math.abs(fm) > 1e-9;
      }
      return Math.abs(m) > 1e-9;
    };
    return new Promise((resolve, reject) => {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Отчет");
      const row1 = worksheet.addRow([
        "Продукт",
        `Остаток на ${moment().format("DD.MM.YYYY")}`,
      ]);
      row1.font = {
        family: 2,
        size: 11,
        bold: true,
      };
      const row2 = worksheet.addRow(["", ""]);
      row2.font = {
        family: 2,
        size: 11,
        bold: true,
      };
      const row3 = worksheet.addRow(["", ""]);
      row3.font = {
        family: 2,
        size: 11,
        bold: true,
      };
      const row4 = worksheet.addRow(["ИТОГО", getters.total.total]);
      row4.font = {
        family: 2,
        size: 11,
        bold: true,
      };
      xlsxNegStyle(row4.getCell(2), getters.total.total);
      const _build: any = {
        hidx: 3,
        h2idx: 3,
        h3idx: 3,
      };
      state.dates.forEach((date: any, idx: number) => {
        const tzAll = getters.total.dates[date.date] || {};
        row1.getCell(_build.hidx).value =
          viewMode === "month" &&
          state.reportDateFrom &&
          state.reportDateTo
            ? `${moment(state.reportDateFrom).format("DD.MM.YYYY")} — ${moment(
                state.reportDateTo,
              ).format("DD.MM.YYYY")}`
            : moment(date.date).format("dd DD.MM");
        date.zones.forEach((zone: any) => {
          const _start = _build.h2idx;
          let _colspan = 0;
          row2.getCell(_build.h2idx).value = zone;
          const zsum = tzAll[zone];
          const zzFlags = date.colspan_zones?.[zone] || {};
          if (zonePlanVisibleExport(zzFlags, zsum)) {
            row3.getCell(_build.h3idx).value = "З";
            const cZ = row4.getCell(_build.h3idx);
            cZ.value = zsum.plan;
            xlsxNegStyle(cZ, zsum.plan);
            _build.h3idx++;
            _colspan++;
            if (params.show_fact) {
              row3.getCell(_build.h3idx).value = "Ф";
              const cF = row4.getCell(_build.h3idx);
              cF.value = zsum.fact_deal;
              xlsxNegStyle(cF, zsum.fact_deal);
              _build.h2idx++;
              _build.h3idx++;
            }
          }
          if (zoneTkVisibleExport(zzFlags, zsum)) {
            row3.getCell(_build.h3idx).value = "ТК";
            const cTk = row4.getCell(_build.h3idx);
            cTk.value = zsum.fact_tk;
            xlsxNegStyle(cTk, zsum.fact_tk);
            _colspan++;
            _build.h3idx++;
          }
          if (zoneMoveVisibleExport(zzFlags, zsum)) {
            row3.getCell(_build.h3idx).value = "П";
            const cP = row4.getCell(_build.h3idx);
            cP.value = zsum.movement;
            xlsxNegStyle(cP, zsum.movement);
            _colspan++;
            _build.h3idx++;
            if (params.show_fact) {
              row3.getCell(_build.h3idx).value = "Ф";
              const cMf = row4.getCell(_build.h3idx);
              cMf.value = zsum.fact_movement;
              xlsxNegStyle(cMf, zsum.fact_movement);
              _build.h2idx++;
              _build.h3idx++;
            }
          }
          if (_start < _build.h2idx) {
            worksheet.mergeCells(2, _start, 2, _build.h2idx);
          }
          _build.h2idx += _colspan;
        });
        if (
          shouldIncludeGreenClientColumns(
            viewMode,
            date,
            state.greenDatesWithData,
          )
        ) {
          state.clients.forEach((client: any) => {
            if (
              !greenClientHasColumnData(
                client,
                date.date,
                viewMode,
                state.dates,
                date,
              )
            ) {
              return;
            }
            row2.getCell(_build.h2idx).value = client.name;
            row2.getCell(_build.h2idx).font = {
              color: { argb: "77AA77" },
            };
            row3.getCell(_build.h3idx).value = "Пл";
            _build.h2idx++;
            const gpv = greenClientPlanTotalForDate(
              client,
              date.date,
              viewMode,
              date,
            );
            const cGp = row4.getCell(_build.h3idx);
            cGp.value = gpv;
            xlsxNegStyle(cGp, gpv);
            _build.h3idx++;
            if (params.show_fact) {
              row3.getCell(_build.h3idx).value = "Ф";
              const gfv = greenClientFactTotalForDate(
                client,
                date.date,
                viewMode,
                date,
              );
              const cGf = row4.getCell(_build.h3idx);
              cGf.value = gfv;
              xlsxNegStyle(cGf, gfv);
              _build.h2idx++;
              _build.h3idx++;
            }
          });
        }

        row2.getCell(_build.h2idx).value = "Остаток";
        const cTot = row4.getCell(_build.h3idx);
        cTot.value = getters.total.dates[date.date].total;
        xlsxNegStyle(cTot, getters.total.dates[date.date].total);
        _build.h2idx++;
        if (_build.hidx < _build.h3idx) {
          worksheet.mergeCells(1, _build.hidx, 1, _build.h3idx);
        }
        _build.h3idx++;
        _build.hidx = _build.h3idx;
      });
      worksheet.mergeCells("A1:A3");
      worksheet.mergeCells("B1:B3");

      state.items.forEach((item: any) => {
        const row = worksheet.addRow([item.sticker_name, item.quantity]);
        xlsxNegStyle(row.getCell(2), item.quantity);

        let _start = 3;
        state.dates.forEach((date: any, idx: number) => {
          const tzd = getters.total.dates[date.date] || {};
          date.zones.forEach((zone: any) => {
            const zt = tzd[zone];
            const zzFlags = date.colspan_zones?.[zone] || {};
            if (zonePlanVisibleExport(zzFlags, zt)) {
              const pv = item.dates[date.date][zone]
                ? item.dates[date.date][zone].plan
                : 0;
              const c1 = row.getCell(_start);
              c1.value = pv;
              xlsxNegStyle(c1, pv);
              _start++;
              if (params.show_fact) {
                const zc = item.dates[date.date][zone];
                const fv = zc
                  ? parseFloat(zc.fact_deal ?? zc.fact_plan) || 0
                  : 0;
                const c2 = row.getCell(_start);
                c2.value = fv;
                xlsxNegStyle(c2, fv);
                _start++;
              }
            }
            if (zoneTkVisibleExport(zzFlags, zt)) {
              const tv = item.dates[date.date][zone]
                ? item.dates[date.date][zone].fact_tk
                : 0;
              const cTk = row.getCell(_start);
              cTk.value = tv;
              xlsxNegStyle(cTk, tv);
              _start++;
            }
            if (zoneMoveVisibleExport(zzFlags, zt)) {
              const mv = item.dates[date.date][zone]
                ? item.dates[date.date][zone].movement
                : 0;
              const cM = row.getCell(_start);
              cM.value = mv;
              xlsxNegStyle(cM, mv);
              _start++;
              if (params.show_fact) {
                const fmv = item.dates[date.date][zone]
                  ? item.dates[date.date][zone].fact_movement
                  : 0;
                const cMf = row.getCell(_start);
                cMf.value = fmv;
                xlsxNegStyle(cMf, fmv);
                _start++;
              }
            }
          });

          if (
            shouldIncludeGreenClientColumns(
              viewMode,
              date,
              state.greenDatesWithData,
            )
          ) {
            state.clients.forEach((client: any) => {
              if (
                !greenClientHasColumnData(
                  client,
                  date.date,
                  viewMode,
                  state.dates,
                  date,
                )
              ) {
                return;
              }
              const spv = greenStickerPlanForDate(
                client,
                item.sticker_id,
                date.date,
                viewMode,
                date,
              );
              const cSp = row.getCell(_start);
              cSp.value = spv;
              xlsxNegStyle(cSp, spv);
              _start++;
              if (params.show_fact) {
                const sfv = greenStickerFactForDate(
                  client,
                  item.sticker_id,
                  date.date,
                  viewMode,
                  date,
                );
                const cSf = row.getCell(_start);
                cSf.value = sfv;
                xlsxNegStyle(cSf, sfv);
                _start++;
              }
            });
          }
          const stTot = item.dates[date.date].total;
          const cSt = row.getCell(_start);
          cSt.value = stTot;
          xlsxNegStyle(cSt, stTot);
          _start++;
        });
        row.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "ffefc0" },
        };
        item.rows.forEach((product: any) => {
          const row = worksheet.addRow([
            `${product.product_name} ${product.product_container_name}`,
            product.quantity,
          ]);
          xlsxNegStyle(row.getCell(2), product.quantity);
          let _start = 3;
          state.dates.forEach((date: any, idx: number) => {
            const tzd = getters.total.dates[date.date] || {};
            date.zones.forEach((zone: any) => {
              const zt = tzd[zone];
              const zzFlags = date.colspan_zones?.[zone] || {};
              if (zonePlanVisibleExport(zzFlags, zt)) {
                const ppv = product.dates[date.date][zone]
                  ? parseFloat(product.dates[date.date][zone].plan) || 0
                  : 0;
                const cP1 = row.getCell(_start);
                cP1.value = ppv;
                xlsxNegStyle(cP1, ppv);
                _start++;
                if (params.show_fact) {
                  const zc = product.dates[date.date][zone];
                  const pfv = zc
                    ? parseFloat(zc.fact_deal ?? zc.fact_plan) || 0
                    : 0;
                  const cP2 = row.getCell(_start);
                  cP2.value = pfv;
                  xlsxNegStyle(cP2, pfv);
                  _start++;
                }
              }
              if (zoneTkVisibleExport(zzFlags, zt)) {
                const ptv = product.dates[date.date][zone]
                  ? parseFloat(product.dates[date.date][zone].fact_tk) || 0
                  : 0;
                const cPtk = row.getCell(_start);
                cPtk.value = ptv;
                xlsxNegStyle(cPtk, ptv);
                _start++;
              }
              if (zoneMoveVisibleExport(zzFlags, zt)) {
                const pmv = product.dates[date.date][zone]
                  ? parseFloat(product.dates[date.date][zone].movement) || 0
                  : 0;
                const cPm = row.getCell(_start);
                cPm.value = pmv;
                xlsxNegStyle(cPm, pmv);
                _start++;
                if (params.show_fact) {
                  const pfmv = product.dates[date.date][zone]
                    ? parseFloat(
                        product.dates[date.date][zone].fact_movement,
                      ) || 0
                    : 0;
                  const cPmf = row.getCell(_start);
                  cPmf.value = pfmv;
                  xlsxNegStyle(cPmf, pfmv);
                  _start++;
                }
              }
            });
            if (
              shouldIncludeGreenClientColumns(
                viewMode,
                date,
                state.greenDatesWithData,
              )
            ) {
              state.clients.forEach((client: any) => {
                if (
                  !greenClientHasColumnData(
                    client,
                    date.date,
                    viewMode,
                    state.dates,
                    date,
                  )
                ) {
                  return;
                }
                const pcIdKey = String(product.product_container_id ?? "");
                const prod =
                  client.stikers[item.sticker_id]?.products?.[pcIdKey];
                const planCell =
                  viewMode === "month" && date.isMonthAggregate
                    ? Number(prod?.plan ?? 0)
                    : Number(prod?.plan_by_date?.[date.date] ?? 0);
                const cPc = row.getCell(_start);
                cPc.value = planCell;
                xlsxNegStyle(cPc, planCell);
                _start++;
                if (params.show_fact) {
                  const factCell =
                    viewMode === "month" && date.isMonthAggregate
                      ? Number(prod?.fact ?? 0)
                      : Number(prod?.fact_by_date?.[date.date] ?? 0);
                  const cFc = row.getCell(_start);
                  cFc.value = factCell;
                  xlsxNegStyle(cFc, factCell);
                  _start++;
                }
              });
            }
            const pTot = product.dates[date.date]?.total;
            const cPt = row.getCell(_start);
            cPt.value = pTot;
            xlsxNegStyle(cPt, pTot);
            _start++;
          });
        });
      });
      worksheet.getColumn(1).width = 40;
      resolve(workbook);
    });
  },

  setFilter({ commit }, term) {
    commit("SET_ITEMS", term);
  },
  toggleSticker({ commit }, idx) {
    commit("TOGGLE_STICKER", idx);
  },

  /** Ручная правка плана по группе зон: одна ячейка. После успеха обновляем только эту ячейку в state (без перезагрузки). */
  saveZonePlanManualQuantity(
    { commit },
    params: {
      deliveryZoneGroupId: number;
      date: string;
      productContainerId: number;
      typeId: number;
      manualQuantity: string | number;
      stickerId: any;
      baseWeight: number;
      zoneKey: string;
      filters: any;
    },
  ) {
    const vm = (this as any)._vm;
    return new Promise((resolve, reject) => {
      vm.$http
        .post(
          "/api/v1/production_forecast/zone_plan_manual_update",
          {
            delivery_zone_group_id: params.deliveryZoneGroupId,
            date: params.date,
            product_container_id: params.productContainerId,
            type_id: params.typeId,
            manual_quantity: params.manualQuantity,
          },
          {},
        )
        .then((response: any) => {
          const value =
            (response?.data?.manual_quantity ?? params.manualQuantity) *
            params.baseWeight;
          commit("PATCH_ZONE_PLAN_CELL", {
            stickerId: params.stickerId,
            productContainerId: params.productContainerId,
            dateStr: params.date,
            zoneKey: params.zoneKey,
            typeId: params.typeId,
            value: Number(value) ?? 0,
          });
          resolve(undefined);
        })
        .catch((err: any) => reject(err));
    });
  },

  /** Ручная правка плана зелёного клиента: кг → целые тары → кг (вес полных тар), обновляем ячейку без перезагрузки. */
  saveManualQuantity(
    { commit },
    params: {
      planIds: number[];
      targets?: any[];
      manualQuantityKg: string | number;
      baseWeight: number;
      clientId: number;
      stickerId: any;
      productId: string;
      dateStr: string;
      filters: any;
    },
  ) {
    const vm = (this as any)._vm;
    const inputKg = parseFloat(String(params.manualQuantityKg)) || 0;
    let manualQuantityKg: number;
    if (params.baseWeight > 0) {
      const containers = Math.ceil(inputKg / params.baseWeight);
      manualQuantityKg = containers * params.baseWeight;
    } else {
      manualQuantityKg = inputKg;
    }

    return new Promise((resolve, reject) => {
      vm.$http
        .post(
          "/api/v1/production_forecast/manual_update",
          {
            plan_ids: params.planIds,
            targets: params.targets || [],
            manual_quantity: manualQuantityKg,
          },
          {},
        )
        .then((response: any) => {
          const data = response?.data;
          if (data?.client_totals?.length) {
            commit("PATCH_CLIENTS_PLAN_FROM_UPDATED_ROWS", data.client_totals);
          }
          commit("PATCH_GREEN_CLIENT_PLAN_CELL", {
            clientId: params.clientId,
            stickerId: params.stickerId,
            productId: params.productId,
            dateStr: params.dateStr,
            value: manualQuantityKg,
          });
          resolve(undefined);
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  },

  transferPlanByDate(
    { commit, dispatch },
    params: {
      sourceDate: string;
      targetDate: string;
      filters: any;
      deliveryZoneGroupId?: number | null;
      greenClientId?: number | null;
    },
  ) {
    const vm = (this as any)._vm;
    const sourceDate = params.sourceDate;
    const targetDate = params.targetDate;
    const key = transferLoadingKey(
      sourceDate,
      targetDate,
      params.deliveryZoneGroupId,
      params.greenClientId,
    );
    const filters = params.filters || {};
    const isFullDay =
      params.deliveryZoneGroupId == null && params.greenClientId == null;

    commit("SET_TRANSFER_LOADING", { key, value: true });
    if (isFullDay) {
      commit("SET_TRANSFER_SOURCE_LOADING", { sourceDate, value: true });
    }
    return new Promise((resolve, reject) => {
      const body: Record<string, unknown> = {
        source_date: sourceDate,
        target_date: targetDate,
        view_mode: "week",
      };
      if (params.deliveryZoneGroupId != null) {
        body.delivery_zone_group_id = params.deliveryZoneGroupId;
      }
      if (params.greenClientId != null) {
        body.green_client_id = params.greenClientId;
      }
      vm.$http
        .post("/api/v1/production_forecast/transfer_day", body, {})
        .then(() => dispatch("loadData", filters))
        .then(() => {
          commit("SET_TRANSFER_LOADING", { key, value: false });
          if (isFullDay) {
            commit("SET_TRANSFER_SOURCE_LOADING", { sourceDate, value: false });
          }
          resolve(undefined);
        })
        .catch((error: any) => {
          commit("SET_TRANSFER_LOADING", { key, value: false });
          if (isFullDay) {
            commit("SET_TRANSFER_SOURCE_LOADING", { sourceDate, value: false });
          }
          reject(error);
        });
    });
  },
};

export const mutations: MutationTree<ShipmentForecastByDateState> = {
  START_LOADING: (state) => {
    state.loaded = false;
    state.loading = true;
    state.fatalError = false;
  },
  LOADED_SUCCESS: (state) => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = false;
  },
  LOADED_ERROR: (state) => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = true;
  },
  SET_TRANSFER_LOADING(
    state,
    payload: { key: string; value: boolean },
  ) {
    Vue.set(state.transferLoadingByDate, payload.key, payload.value);
  },
  SET_TRANSFER_SOURCE_LOADING(
    state,
    payload: { sourceDate: string; value: boolean },
  ) {
    Vue.set(
      state.transferLoadingBySourceDate,
      payload.sourceDate,
      payload.value,
    );
  },

  TOGGLE_STICKER(state, idx) {
    state.items[idx].expanded = !state.items[idx].expanded;
  },

  /** Обновить одну ячейку плана зелёного клиента (без перезагрузки). */
  PATCH_GREEN_CLIENT_PLAN_CELL(
    state,
    payload: {
      clientId: number;
      stickerId: any;
      productId: string;
      dateStr: string;
      value: number;
    },
  ) {
    const client = state.clients.find(
      (c: any) => Number(c.id) === Number(payload.clientId),
    );
    if (!client?.stikers?.[payload.stickerId]?.products?.[payload.productId])
      return;
    const prod = client.stikers[payload.stickerId].products[payload.productId];
    if (!prod.plan_by_date) prod.plan_by_date = {};
    prod.plan_by_date[payload.dateStr] = payload.value;
    prod.plan = Object.values(prod.plan_by_date).reduce(
      (s: number, v: any) => s + Number(v || 0),
      0,
    );
  },

  PATCH_CLIENTS_PLAN_FROM_UPDATED_ROWS(state, clientTotals: any[]) {
    const byClient = (clientTotals as any[]).reduce((acc: any, row: any) => {
      acc[row.client_id] = {
        plan: Number(row.plan ?? 0),
        plan_manual: Number(row.plan_manual ?? 0),
      };
      return acc;
    }, {} as any);
    state.clients = state.clients.map((c: any) => {
      const patch = byClient[c.id];
      if (!patch) return c;
      return { ...c, plan: patch.plan, plan_manual: patch.plan_manual };
    });
  },

  /** Обновить одну ячейку плана по зоне (plan или movement) без перезагрузки. */
  PATCH_ZONE_PLAN_CELL(
    state,
    payload: {
      stickerId: any;
      productContainerId: number;
      dateStr: string;
      zoneKey: string;
      typeId: number;
      value: number;
    },
  ) {
    const sticker = state.items.find(
      (s: any) => String(s.sticker_id) === String(payload.stickerId),
    );
    if (!sticker?.rows) return;
    const row = sticker.rows.find(
      (r: any) =>
        Number(r.product_container_id) === Number(payload.productContainerId),
    );
    if (!row?.dates?.[payload.dateStr]?.[payload.zoneKey]) return;
    const cell = row.dates[payload.dateStr][payload.zoneKey];
    const oldVal =
      payload.typeId === 1
        ? Number(cell.plan ?? 0)
        : Number(cell.movement ?? 0);
    const delta = payload.value - oldVal;
    if (payload.typeId === 1) {
      cell.plan = payload.value;
    } else if (payload.typeId === 4) {
      cell.movement = payload.value;
    }
    if (sticker.dates?.[payload.dateStr]?.[payload.zoneKey] && delta !== 0) {
      const agg = sticker.dates[payload.dateStr][payload.zoneKey];
      if (payload.typeId === 1) {
        agg.plan = (agg.plan ?? 0) + delta;
      } else {
        agg.movement = (agg.movement ?? 0) + delta;
      }
    }
    const dateObj = state.dates.find(
      (d: any) => d.type === "date" && d.date === payload.dateStr,
    );
    if (dateObj?.colspan_zones?.[payload.zoneKey] && delta !== 0) {
      if (payload.typeId === 1) {
        dateObj.colspan_zones[payload.zoneKey].plan =
          (dateObj.colspan_zones[payload.zoneKey].plan ?? 0) + delta;
      } else {
        dateObj.colspan_zones[payload.zoneKey].movement =
          (dateObj.colspan_zones[payload.zoneKey].movement ?? 0) + delta;
      }
    }
  },

  SET_REPORT_DATA(state, data) {
    state.data = data.documents;
    state.dates = [];
    state.viewMode = data.viewMode || "week";
    state.reportDateFrom = data.date_from || null;
    state.reportDateTo = data.date_to || null;
    state.clients = data.clients_list.filter((client: any) => {
      return client.plan > 0 || client.plan_manual > 0;
    });

    if (state.viewMode === "month") {
      state.dates.push({
        type: "date",
        date: data.date_from,
        zones: [],
        colspan_zones: {},
        colspan: 1,
        isMonthAggregate: true,
      });
      const daysCollapsed =
        moment(data.date_to).diff(moment(data.date_from), "days") + 1;
      if (typeof console !== "undefined" && console.debug) {
        console.debug("[ShipmentForecastByDate] month aggregate", {
          date_from: data.date_from,
          date_to: data.date_to,
          daysCollapsed,
          zonesCount: "after SET_ITEMS",
        });
      }
    } else {
      for (
        let i = 0;
        i <= moment(data.date_to).diff(moment(data.date_from), "days");
        i++
      ) {
        state.dates.push({
          type: "date",
          date: moment(data.date_from)
            .add(i, "days")
            .format("YYYY-MM-DD"),
          zones: [],
          colspan_zones: {},
          colspan: 1,
        });
      }
    }
  },

  SET_ITEMS(state, term) {
    const dates = state.dates.reduce((acc: any, item: any) => {
      if (item.type === "date") {
        acc[item.date] = item;
      }
      return acc;
    }, {});
    const isMonth = state.viewMode === "month";
    const monthFrom = state.reportDateFrom;
    const monthTo = state.reportDateTo;
    state.items = Object.values(
      state.data.reduce((stikers: any, item: any) => {
        if (!stikers[item.sticker_id]) {
          stikers[item.sticker_id] = {
            sticker_id: item.sticker_id,
            sticker_name: item.sticker_id ? item.sticker_name : "Без стикера",
            expanded: false,
            quantity: 0,
            dates: {},
            rows: [],
          };
        }
        item.quantity = parseFloat(item.quantity) || 0;
        stikers[item.sticker_id].quantity += item.quantity;
        const input = {
          total: item.quantity,
        };
        const item_dates = item.dates.reduce((data: any, row: any) => {
          data[row.delivery_date] = row;
          return data;
        }, {});
        item.dates = Object.keys(dates).reduce((data: any, item_date: any) => {
          const row =
            isMonth && monthFrom && monthTo
              ? mergeItemDatesToSingleRow(item_dates, monthFrom, monthTo)
              : item_dates[item_date] || { zones: [] };
          if (!stikers[item.sticker_id].dates[item_date]) {
            stikers[item.sticker_id].dates[item_date] = {
              total: 0,
            };
          }

          let zonePlanSubtracted = 0;
          let movementSubtracted = 0;
          const openingQuantity = input.total;
          data[item_date] = row.zones.reduce(
            (acc: any, zone: any) => {
              const zoneCell = zone.data?.[0] || {};
              const plan = parseFloat(zoneCell.plan) || 0;
              const fact_tk = parseFloat(zoneCell.fact_tk) || 0;
              const fact_deal = parseFloat(zoneCell.fact_deal) || 0;
              const fact_movement = parseFloat(zoneCell.fact_movement) || 0;
              const movement = parseFloat(zoneCell.movement) || 0;
              const normalizedZoneCell = {
                ...zoneCell,
                zone_id: zoneCell.zone_id ?? zone.zone_id,
                plan,
                fact_tk,
                fact_deal,
                fact_movement,
                movement,
              };
              if (
                plan !== 0 ||
                fact_tk !== 0 ||
                movement !== 0 ||
                fact_deal !== 0 ||
                fact_movement !== 0
              ) {
                acc[zone.zone_name] = normalizedZoneCell;

                if (
                  !stikers[item.sticker_id].dates[item_date][zone.zone_name]
                ) {
                  stikers[item.sticker_id].dates[item_date][zone.zone_name] = {
                    id: zone.zone_id,
                    plan: 0,
                    fact_tk: 0,
                    fact_deal: 0,
                    fact_movement: 0,
                    movement: 0,
                  };
                }

                if (!dates[item_date].colspan_zones[zone.zone_name]) {
                  dates[item_date].colspan_zones[zone.zone_name] = {
                    id: zone.zone_id,
                    plan: 0,
                    fact_tk: 0,
                    fact_deal: 0,
                    fact_movement: 0,
                    movement: 0,
                    any_plan_or_fact_deal_cell: false,
                    any_fact_tk_cell: false,
                  };
                }
                dates[item_date].colspan_zones[zone.zone_name].plan += plan;
                dates[item_date].colspan_zones[
                  zone.zone_name
                ].fact_deal += fact_deal;
                dates[item_date].colspan_zones[
                  zone.zone_name
                ].fact_movement += fact_movement;
                dates[item_date].colspan_zones[
                  zone.zone_name
                ].fact_tk += fact_tk;
                dates[item_date].colspan_zones[
                  zone.zone_name
                ].movement += movement;
                const czAgg = dates[item_date].colspan_zones[zone.zone_name];
                if (plan !== 0 || fact_deal !== 0) {
                  czAgg.any_plan_or_fact_deal_cell = true;
                }
                if (fact_tk !== 0) {
                  czAgg.any_fact_tk_cell = true;
                }
                stikers[item.sticker_id].dates[item_date][
                  zone.zone_name
                ].plan += plan;
                stikers[item.sticker_id].dates[item_date][
                  zone.zone_name
                ].fact_tk += fact_tk;
                stikers[item.sticker_id].dates[item_date][
                  zone.zone_name
                ].fact_deal += fact_deal;
                stikers[item.sticker_id].dates[item_date][
                  zone.zone_name
                ].fact_movement += fact_movement;
                stikers[item.sticker_id].dates[item_date][
                  zone.zone_name
                ].movement += movement;
                if (!dates[item_date].zones.includes(zone.zone_name)) {
                  dates[item_date].zones.push(zone.zone_name);
                }
                /* Остаток: только план зоны и перенос (без fact_tk / fact_deal / fact_movement). */
                acc.total -= plan;
                acc.total -= movement;
                zonePlanSubtracted += plan;
                movementSubtracted += movement;
              }
              return acc;
            },
            {
              total: input.total,
            },
          );
          const greenPlanSubtracted = sumGreenPlanForProductRow(
            state.clients,
            item.sticker_id,
            item.product_container_id,
            item_date,
            state.viewMode,
            dates[item_date],
          );
          data[item_date].total -= greenPlanSubtracted;
          input.total = data[item_date].total;
          stikers[item.sticker_id].dates[item_date].total += input.total;

          const dbgMonth = dates[item_date]?.isMonthAggregate;
          if (
            dbgMonth &&
            typeof localStorage !== "undefined" &&
            localStorage.getItem("DEBUG_SHIPMENT_FORECAST_BALANCE") === "1" &&
            typeof console !== "undefined" &&
            console.debug
          ) {
            console.debug("[ShipmentForecastByDate] row balance check", {
              product_container_id: item.product_container_id,
              sticker_id: item.sticker_id,
              openingQuantity,
              zonePlanSubtracted,
              movementSubtracted,
              greenPlanSubtracted,
              total: data[item_date].total,
            });
          }
          return data;
        }, {});
        stikers[item.sticker_id].rows.push(item);
        return stikers;
      }, {}),
    ).sort((a: any, b: any) => {
      const keyA = a.sticker_id ? a.sticker_name.toLowerCase() : "я";
      const keyB = b.sticker_id ? b.sticker_name.toLowerCase() : "я";
      return keyA.localeCompare(keyB, "en");
    });
    state.clients.map((item: any) => {
      item.plan = 0;
      item.plan_manual = 0;
      item.stikers = {};
      item.plans.map((plan: any) => {
        const sticker_id: any =
          plan.sticker_ids && plan.sticker_ids.length > 0
            ? parseInt(plan.sticker_ids[0].replace("d:voc_stickers:", ""))
            : null;
        if (!item.stikers[sticker_id]) {
          item.stikers[sticker_id] = {
            plan: 0,
            fact: 0,
            plan_manual: 0,
            products: {},
          };
        }
        item.stikers[sticker_id].plan += parseFloat(plan.plan) || 0;
        item.stikers[sticker_id].fact += parseFloat(plan.fact) || 0;
        item.stikers[sticker_id].plan_manual +=
          parseFloat(plan.plan_manual) || 0;
        item.plan += parseFloat(plan.plan) || 0;
        item.plan_manual += parseFloat(plan.plan_manual) || 0;
        // Учёт по таре продукта (product_container_id), не по продукту
        if (plan.product_container_id) {
          const productContainerIdKey = String(
            plan.product_container_id.replace("d:product_containers:", ""),
          );
          const fromIds = plan.plan_ids_for_date || {};
          const fromByDate = plan.plan_by_date || {};
          item.stikers[sticker_id].products[productContainerIdKey] = {
            product_container_id: productContainerIdKey,
            plan: plan.plan,
            fact: plan.fact,
            plan_manual: plan.plan_manual,
            plan_ids_for_date: { ...fromIds },
            plan_by_date: { ...fromByDate },
            fact_by_date: { ...(plan.fact_by_date || {}) },
          };
        }
      });
    });

    const sohZoneShell = () => ({
      id: null as number | null,
      plan: 0,
      fact_tk: 0,
      fact_deal: 0,
      fact_movement: 0,
      movement: 0,
      sell_plan: 0,
      sell_plan_manual: 0,
      /** Any product row had non-zero plan or fact_deal (not only positive); avoids hiding cols when totals cancel. */
      any_plan_or_fact_deal_cell: false,
      any_fact_tk_cell: false,
    });
    Object.values(dates).forEach((d: any) => {
      if (d.type !== "date") return;
      SHIP_FORECAST_SOH_ZONE_NAMES.forEach((zn: string) => {
        if (!d.colspan_zones[zn]) {
          d.colspan_zones[zn] = sohZoneShell();
        }
        if (!d.zones.includes(zn)) {
          d.zones.push(zn);
        }
      });
    });

    state.items.forEach((sticker: any) => {
      padShipmentItemZonesForDates(sticker, dates);
      (sticker.rows || []).forEach((row: any) =>
        padShipmentItemZonesForDates(row, dates),
      );
    });

    const greenDatesWithData: Record<string, boolean> = {};
    state.clients.forEach((client: any) => {
      Object.values(client?.stikers || {}).forEach((st: any) => {
        Object.values(st?.products || {}).forEach((product: any) => {
          Object.entries(product?.plan_by_date || {}).forEach(
            ([dateKey, rawValue]: [string, any]) => {
              if (Number(rawValue || 0) > 0) greenDatesWithData[dateKey] = true;
            },
          );
        });
      });
    });
    state.greenDatesWithData = greenDatesWithData;

    state.dates.forEach((item: any) => {
      item.colspan = 1;
      Object.keys(item.colspan_zones).forEach((zone_name: any) => {
        const zz = item.colspan_zones[zone_name];
        const showPlanCol =
          !!zz.any_plan_or_fact_deal_cell || zz.plan + zz.fact_deal > 0;
        item.colspan += showPlanCol ? 1 : 0;
        item.colspan += zz.any_fact_tk_cell || zz.fact_tk > 0 ? 1 : 0;
        item.colspan +=
          Math.abs(Number(zz.movement) || 0) > 1e-9 ||
            Math.abs(Number(zz.fact_movement) || 0) > 1e-9
            ? 1
            : 0;
        item.colspan +=
          item.colspan_zones[zone_name].sell_plan > 0 ||
          item.colspan_zones[zone_name].sell_plan_manual > 0;
      });
      item.colspan += countGreenClientColumnsForDate(
        state.clients,
        state.viewMode,
        state.dates,
        item,
        state.greenDatesWithData,
      );
    });
  },
};

const getters = {
  total: (state: any) => {
    const total: any = {
      total: 0,
      dates: {},
    };
    state.items.forEach((r: any) => {
      // t.rows.forEach((r: any) => {
      total.total += parseFloat(r.quantity);
      Object.keys(r.dates).forEach((key: any) => {
        if (!total.dates[key]) {
          total.dates[key] = {};
        }
        Object.keys(r.dates[key]).forEach((zone_name: any) => {
          if (["total", "quantity"].includes(zone_name)) {
            if (!total.dates[key][zone_name]) {
              total.dates[key][zone_name] = 0;
            }
            total.dates[key][zone_name] +=
              parseFloat(r.dates[key][zone_name]) || 0;
          } else {
            if (!total.dates[key][zone_name]) {
              total.dates[key][zone_name] = {
                plan: 0,
                fact_tk: 0,
                fact_deal: 0,
                fact_movement: 0,
                movement: 0,
              };
            }
            total.dates[key][zone_name].plan +=
              parseFloat(r.dates[key][zone_name].plan) || 0;
            total.dates[key][zone_name].fact_tk +=
              parseFloat(r.dates[key][zone_name].fact_tk) || 0;
            total.dates[key][zone_name].fact_deal +=
              parseFloat(r.dates[key][zone_name].fact_deal) || 0;
            total.dates[key][zone_name].fact_movement +=
              parseFloat(r.dates[key][zone_name].fact_movement) || 0;
            total.dates[key][zone_name].movement +=
              parseFloat(r.dates[key][zone_name].movement) || 0;
          }
        });
      });
    });
    state.dates.forEach((d: any) => {
      if (d.type !== "date") return;
      const key = d.date;
      if (!total.dates[key]) {
        total.dates[key] = {};
      }
      (d.zones || []).forEach((zn: string) => {
        if (["total", "quantity"].includes(zn)) return;
        if (!total.dates[key][zn]) {
          total.dates[key][zn] = {
            plan: 0,
            fact_tk: 0,
            fact_deal: 0,
            fact_movement: 0,
            movement: 0,
          };
        }
      });
      const tt = total.dates[key].total;
      if (tt == null || Number.isNaN(Number(tt))) {
        total.dates[key].total = 0;
      }
    });
    // })
    return total;
  },
};

export const STORE_KEY = "$_shipment_forecast_by_date";

export const store = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
};
